import { useEffect, useState } from "react";
import { api } from "../api/client.js";
import type { OnboardingState, User } from "../api/types.js";
import { AuthScreen } from "../features/auth/AuthScreen.js";
import { OnboardingPage } from "../features/profile/onboarding/OnboardingPage.js";
import { WorkspaceShell } from "./WorkspaceShell.js";
import { PaperDetailPage } from "../features/works/PaperDetailPage.js";
import { LegalPage } from "../features/auth/LegalPage.js";
import "../styles/base.css";
import "../styles/auth.css";
import "../styles/sidebar.css";
import "../styles/workspace.css";
import "../styles/chat.css";
import "../styles/composer.css";
import "../styles/sources.css";
import "../styles/skills.css";
import "../styles/onboarding.css";
import "../styles/responsive.css";
import "../styles/paper.css";

export function App() {
  const legalMatch = window.location.pathname.match(/^\/legal\/(terms|privacy)\/?$/);
  const [booting, setBooting] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [onboarding, setOnboarding] = useState<OnboardingState | null>(null);
  // onboarding 视图是否挂载由 boot 时一次性决定,之后只被 onSkipped/onCompleted 关闭;
  // 状态流式变成 completed 时不自动切走,让用户读完最终总结再进工作台。
  const [onboardingActive, setOnboardingActive] = useState(false);

  const refreshOnboarding = async () => {
    const data = await api<{ onboarding: OnboardingState }>("/api/profile/onboarding");
    setOnboarding(data.onboarding);
    return data.onboarding;
  };

  const boot = async () => {
    setBooting(true);
    try {
      const data = await api<{ user: User }>("/api/auth/me");
      setUser(data.user);
      const onboardingState = await refreshOnboarding();
      setOnboardingActive(onboardingState.status === "pending");
    } catch {
      setUser(null);
      setOnboarding(null);
      setOnboardingActive(false);
    } finally {
      setBooting(false);
    }
  };

  useEffect(() => { boot(); }, []);

  const leaveOnboarding = () => {
    setOnboardingActive(false);
    void refreshOnboarding();
  };

  if (legalMatch) return <LegalPage kind={legalMatch[1] as "terms" | "privacy"} />;
  if (booting) return <main className="boot-screen">Truth Seeker</main>;
  if (!user) return <AuthScreen onAuthed={boot} />;
  if (onboardingActive && onboarding) {
    return (
      <OnboardingPage
        initialState={onboarding}
        onStateChange={setOnboarding}
        onCompleted={leaveOnboarding}
        onSkipped={leaveOnboarding}
      />
    );
  }
  const paperMatch = window.location.pathname.match(/^\/papers\/([^/]+)\/?$/);
  if (paperMatch) {
    const sessionId = new URLSearchParams(window.location.search).get("sessionId");
    return <PaperDetailPage workId={decodeURIComponent(paperMatch[1])} sessionId={sessionId} />;
  }
  return <WorkspaceShell initialUser={user} onLogout={() => setUser(null)} />;
}
